import { Image, Text, Box, VStack, HStack, Divider } from "@chakra-ui/react";
import { LaurelIcon } from "../../icons/LaurelIcon";
import { useState, useEffect } from "react";
import { StarDisplay } from "../../common/StarDisplay";
import { PeoplePile } from "../../user/PeoplePile";
import { TestimonialReviewType, TestimonialType } from "../types";

export function Testimonials({
  testimonials,
}: {
  testimonials: TestimonialType;
}) {
  const isRedesign = !!testimonials.subtitle;

  if (isRedesign) {
    return (
      <VStack gap="23px" px={6} width="100%" alignItems="center">
        {/* Title header with App Store rating */}
        <VStack gap="13px" alignItems="center" width="100%" flexWrap="wrap">
          <Text
            fontFamily="'Bricolage Grotesque', sans-serif"
            fontWeight={500}
            fontSize={{ base: "20px", md: "24px" }}
            lineHeight="1.1"
            color="white"
            textAlign="center"
          >
            {testimonials.title}
          </Text>
          {testimonials.app_store_rating && (
            <HStack gap="1px" alignItems="center" justifyContent="center">
              <LaurelIcon w="19px" h="31px" color="white" transform="rotate(20deg)" />
              <VStack gap={0} alignItems="center">
                <Text fontSize="12px" fontWeight={300} lineHeight="1.2" color="neutral.300">
                  {testimonials.app_store_rating_label}
                </Text>
                <Text fontSize="14px" fontWeight={700} lineHeight="1.38" color="white">
                  {testimonials.app_store_rating} ★★★★★
                </Text>
              </VStack>
              <LaurelIcon w="19px" h="31px" color="white" transform="rotate(160deg) scaleY(-1)" />
            </HStack>
          )}
        </VStack>

        {/* Review section */}
        <VStack gap={3} py={4} width="100%" borderRadius="24px" alignItems="center">
          <VStack gap={3} alignItems="center" width="100%">
            <PeoplePile
              readerAvatarsUrls={testimonials.readers_avatars_urls}
              readersCount={testimonials.readers_count_string}
            />
            <Text fontSize="12px" lineHeight="16px" color="neutral.400" textAlign="center">
              {testimonials.subtitle}
            </Text>
          </VStack>
          <Divider borderColor="transparent.white.10" width="294px" />
          <Box px="30px" width="100%">
            <TestimonialCarousel reviews={testimonials.reviews} />
          </Box>
        </VStack>
      </VStack>
    );
  }

  // Original bordered layout
  return (
    <Box px={6} py={2} width="100%" borderRadius="24px">
      <VStack
        width="100%"
        gap={4}
        p={6}
        borderRadius="24px"
        border="1px solid"
        borderColor="transparent.white.10"
        background="transparent.black.20"
      >
        <PeoplePile
          readerAvatarsUrls={testimonials.readers_avatars_urls}
          readersCount={testimonials.readers_count_string}
        />
        <Text variant="paymentTestimonialTitle">{testimonials.title}</Text>
        <Divider borderColor="transparent.white.10" />
        <TestimonialCarousel reviews={testimonials.reviews} />
      </VStack>
    </Box>
  );
}

export function TestimonialCarousel({
  reviews,
  interval = 8000,
}: {
  reviews: TestimonialReviewType[];
  interval?: number;
}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % reviews.length);
    }, interval);
    return () => clearInterval(timer);
  }, [reviews.length, interval]);

  const review = reviews[index];

  return (
    <Box w="full" position="relative">
      <VStack
        key={index}
        w="full"
        gap={2}
        sx={{
          animation: "fadeIn 0.6s ease-in-out",
          "@keyframes fadeIn": {
            "0%": { opacity: 0 },
            "100%": { opacity: 1 },
          },
        }}
      >
        <HStack
          alignItems="center"
          justifyContent="space-between"
          width="full"
        >
          <HStack alignItems="center" gap={2}>
            <Image
              src={review.reviewer.avatar_url}
              boxSize="32px"
              alt={review.reviewer.name}
              borderRadius="full"
            />
            <Text
              fontWeight="700"
              fontSize="14px"
              lineHeight="11px"
              color="neutral.400"
            >
              {review.reviewer.name}
            </Text>
          </HStack>
          <StarDisplay rating={review.rating} />
        </HStack>
        <Text
          fontSize="14px"
          fontStyle="italic"
          lineHeight="1.5"
          color="neutral.300"
          textAlign="left"
          width="full"
        >
          "{review.text}"
        </Text>
      </VStack>
    </Box>
  );
}
